import { UtensilsCrossed } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { SEO, SITE } from "@/config/site";
import { Fade, Lines, Reveal } from "@/components/motion";
import { BookingButton, WhatsAppAssist } from "@/components/CTA";

const CONSIDERS = [
  "Obiettivi personali",
  "Abitudini alimentari",
  "Stile di vita",
  "Preferenze personali",
  "Informazioni cliniche, quando pertinenti",
  "Attività fisica e pratica sportiva",
];

const GOALS = [
  "Migliorare l'alimentazione",
  "Perdere peso in modo sostenibile",
  "Migliorare la composizione corporea",
  "Supportare l'attività sportiva",
  "Organizzare meglio i pasti",
  "Sviluppare abitudini alimentari durature",
];

export default function Nutrizione() {
  useSEO(SEO.nutrizione);
  return (
    <>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-20 sm:pb-28">
        <div className="grid items-center gap-14 lg:grid-cols-[1.15fr_1fr] lg:gap-20">
          <div>
            <Fade>
              <p className="eyebrow text-leaf-deep">Nutrizione · Bologna e Casalecchio di Reno</p>
            </Fade>
            <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              <Lines delay={0.2} lines={["Nutrizione", <em key="e" className="text-leaf-deep">personalizzata</em>]} />
            </h1>
            <Fade delay={0.6}>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/65">
                Non una dieta standard: un percorso alimentare costruito sulla persona — obiettivi,
                abitudini, stile di vita e contesto quotidiano — con la scienza come punto di
                partenza e la sostenibilità come obiettivo.
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/60">
                Ogni piano viene monitorato nel tempo e adattato quando serve, perché
                un'alimentazione efficace è prima di tutto un'alimentazione che si riesce a vivere.
              </p>
            </Fade>
          </div>
          <Fade delay={0.4} className="relative">
            <img
              src={SITE.images.food}
              alt="Piatto equilibrato con salmone, cereali e verdure fresche: la nutrizione personalizzata di Nutrimove Lab"
              className="w-full [filter:drop-shadow(0_28px_36px_rgba(16,22,20,0.14))]"
              width={1100}
              height={1375}
              fetchPriority="high"
              data-testid="nutrition-visual"
            />
          </Fade>
        </div>
      </section>

      <section className="border-t border-ink/10" aria-label="Come costruisco i piani alimentari">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal className="max-w-3xl">
            <p className="eyebrow text-wave-deep">L'approccio Nutrimove Lab</p>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Piani costruiti artigianalmente, non assemblati
            </h2>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-ink/65">
              Ogni piano alimentare Nutrimove Lab nasce da zero: non esistono modelli
              preconfezionati da adattare, ma una logica di lavoro precisa, pratica e sostenibile
              con cui organizzo l'alimentazione intorno alla persona.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink/60">
              È la stessa impostazione operativa che applico personalmente alla mia organizzazione
              alimentare di tutti i giorni: un sistema reale, usabile nella vita vera, non solo
              sulla carta.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-x-12 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Nessun modello standard", "Ogni piano parte dalla valutazione della persona, mai da un template."],
              ["Costruzione individuale", "Abitudini, preferenze e obiettivi danno forma a ogni scelta."],
              ["Monitoraggio continuo", "Il piano viene seguito e valutato nel tempo, insieme."],
              ["Sostenibilità reale", "L'obiettivo è un'alimentazione mantenibile, non un sacrificio temporaneo."],
            ].map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="border-t-2 border-leaf/60 pt-5">
                  <h3 className="text-sm font-bold uppercase tracking-[0.16em]">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-mist" aria-label="Come viene costruito il percorso">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 py-20 sm:py-28 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Il percorso tiene conto di
            </h2>
            <ul className="mt-9 flex flex-col">
              {CONSIDERS.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-4 border-t border-ink/10 py-4 text-sm sm:text-base text-ink/75 last:border-b"
                >
                  <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-leaf" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Obiettivi possibili
            </h2>
            <ul className="mt-9 flex flex-col">
              {GOALS.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-4 border-t border-ink/10 py-4 text-sm sm:text-base text-ink/75 last:border-b"
                >
                  <UtensilsCrossed aria-hidden="true" className="h-4 w-4 shrink-0 text-leaf-deep" strokeWidth={1.75} />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Le fotografie dello studio potranno essere aggiunte qui in futuro,
          senza modificare il resto della pagina. */}

      <section className="border-t border-ink/10" aria-label="Prenota una visita nutrizionale">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 py-20 sm:py-28 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Vuoi migliorare la tua alimentazione con un piano su misura?
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60">
              Il primo passo è una prima visita: raccoglieremo le informazioni utili, definiremo i
              tuoi obiettivi e imposteremo il percorso alimentare più adatto a te.
            </p>
            <div className="mt-9">
              <BookingButton label="Prenota una visita nutrizionale" testId="nutrition-booking-cta" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <WhatsAppAssist testId="nutrition-whatsapp-assist" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
